import { useEffect } from 'react';
import { createApiRequest } from '../utils/apiUtils';
import { handleAuthFailure, isReloginRequired } from '../Redux/Auth/authApi';

// セッション検証の間隔（ミリ秒）。削除済みユーザーをこの間隔以内に検出してログアウトする。
const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * セッションの有効性を定期的に検証するハートビート。feast-fe ADR 0014。
 *
 * `GET /clerk/session` を定期・画面復帰時に呼ぶ。削除済みユーザーは BE が 401 を返し、
 * createApiRequest 内のリフレッシュも 401（在籍なし）となるため、既存の handleAuthFailure により
 * セッション破棄＋ /login への遷移（＝即時ログアウト）が自動的に行われる。
 *
 * ネットワーク一時障害など 401 以外のエラーでは何もしない（誤ログアウト防止）。
 */
export function useSessionHeartbeat(): void {
  useEffect(() => {
    const check = async () => {
      // 未ログイン時はスキップ（無駄な呼び出し・誤遷移を避ける）。
      if (!localStorage.getItem('accessToken')) return;
      // 認証 localStorage 変更による強制再ログイン（feast-fe ADR 0019）。
      // 適用日時より前のログインはセッション破棄＋/login へ遷移する。
      if (isReloginRequired()) {
        handleAuthFailure();
        return;
      }
      try {
        await createApiRequest('/clerk/session', { method: 'GET' });
      } catch {
        // 認証失効（削除済み等）の場合のログアウトは createApiRequest 内の
        // handleAuthFailure が担う。ここでは何もしない（誤ログアウト防止）。
      }
    };

    check();
    const timer = window.setInterval(check, HEARTBEAT_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
}
